import { VITE_API_KEY, VITE_BACKEND_REST_URL } from '@src/main/env';
import { logger } from '@src/main/shared/utils/logger';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

export type TokenProvider = {
  getToken: () => Promise<string>;
};

interface ApiConfig extends AxiosRequestConfig {
  token?: string;
}

class ApiService {
  private static async request<T>(config: ApiConfig): Promise<T> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-api-key': VITE_API_KEY,
        source: 'web',
        ...(config.token && { Authorization: `${config.token}` }),
        ...config.headers,
      };

      const response: AxiosResponse<T> = await axios({
        baseURL: VITE_BACKEND_REST_URL,
        ...config,
        headers,
      });

      return response.data;
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  private static handleError(error: AxiosError): void {
    if (error.response?.status === 401) {
      window.location.href = '/login';
      return;
    }

    logger.error('API Error:', {
      message: error.message,
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
    });

    throw error;
  }

  static async get<T>(endpoint: string, token: string, params?: unknown): Promise<T> {
    return this.request<T>({
      method: 'GET',
      url: endpoint,
      params,
      token,
    });
  }

  static async post<T>(
    endpoint: string,
    token: string,
    data?: unknown,
    isFormData = false,
  ): Promise<T> {
    return this.request<T>({
      method: 'POST',
      url: endpoint,
      data,
      token,
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    });
  }

  static async put<T = void>(endpoint: string, token: string, data?: unknown): Promise<T> {
    return this.request<T>({
      method: 'PUT',
      url: endpoint,
      data,
      token,
    });
  }

  static async patch<T = void>(endpoint: string, token: string, data?: unknown): Promise<T> {
    return this.request<T>({
      method: 'PATCH',
      url: endpoint,
      data,
      token,
    });
  }

  static async delete(endpoint: string, token: string): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: endpoint,
      token,
    });
  }

  static async fetchBlob(endpoint: string, token: string, params?: unknown): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await axios({
        baseURL: VITE_BACKEND_REST_URL,
        url: endpoint,
        method: 'GET',
        responseType: 'blob',
        params,
        headers: {
          source: 'web',
          'x-api-key': VITE_API_KEY,
          ...(token && { Authorization: `${token}` }),
        },
      });

      return response.data;
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }
}

export const api = ApiService;
